import axios from 'axios';
import { API_BASE_URL } from '../config';

// In development, prefer CRA proxy (/api/*) to avoid CORS/SSL issues.
// In production builds, use the absolute API_BASE_URL.
const apiBase = () => (process.env.NODE_ENV === 'development' ? '' : API_BASE_URL);

export const loginUser = async (username, password, captchaToken) => {
  return await axios.post(`${API_BASE_URL}/api/auth/odoo-login`, {
    username,
    password,
    recaptchaToken: captchaToken,
  });
};

// =============================================
// NEW: Two-step employee login (National ID + Password + OTP)
// - initiate: verifies credentials then sends OTP to linked mobile
// - verify: verifies OTP and returns JWT
// Notes:
// - Backend field names may vary; we attempt safe variants.
// =============================================

export type EmployeeInitiateOtpResponse = {
  challengeId: string;
  maskedMobile?: string | null;
  message?: string | null;
  raw?: any;
};

export type EmployeeVerifyOtpResponse = {
  token: string;
  empName?: string | null;

  user?: {
    id?: number;
    name?: string;
    email?: string;
    phone?: string;
    nationalID?: string;
    dob?: string;
    nationality?: string | null;
  } | null;

  donor?: any;
  continueResult?: any;
  raw?: any;
};

function pickFirstString(obj: any, keys: string[]): string | null {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

function normalizeInitiateOtpResponse(data: any): EmployeeInitiateOtpResponse {
  const d = data && typeof data === 'object' ? data : {};
  const challengeId =
    pickFirstString(d, ['challengeId', 'ChallengeId', 'challenge_id', 'id', 'Id']) ||
    pickFirstString(d?.result, ['challengeId', 'ChallengeId', 'challenge_id', 'id', 'Id']) ||
    '';

  const maskedMobile =
    pickFirstString(d, ['maskedMobile', 'MaskedMobile', 'mobileMasked', 'masked_phone', 'maskedPhone']) ||
    pickFirstString(d?.result, ['maskedMobile', 'MaskedMobile', 'mobileMasked', 'masked_phone', 'maskedPhone']);

  const message =
    pickFirstString(d, ['message', 'Message', 'msg']) ||
    pickFirstString(d?.result, ['message', 'Message', 'msg']);

  if (!challengeId) {
    // Keep raw so UI can show a reasonable error if server changed.
    throw new Error('Unexpected initiate-otp payload (missing challengeId)');
  }

  return { challengeId, maskedMobile, message, raw: data };
}

function normalizeVerifyOtpResponse(data: any): EmployeeVerifyOtpResponse {
  const d = data && typeof data === 'object' ? data : {};
  const token =
    pickFirstString(d, ['token', 'Token', 'jwt', 'accessToken', 'access_token']) ||
    pickFirstString(d?.result, ['token', 'Token', 'jwt', 'accessToken', 'access_token']) ||
    '';

  const empName =
    pickFirstString(d, ['empName', 'EmpName', 'employeeName', 'name', 'Name']) ||
    pickFirstString(d?.result, ['empName', 'EmpName', 'employeeName', 'name', 'Name']);

  if (!token) throw new Error('Unexpected verify-otp payload (missing token)');
    return {
    token,
    empName,
    user: d.user ?? null,
    donor: d.donor ?? null,
    continueResult: d.continueResult ?? null,
    raw: data,
  };

}

async function postWithPayloadVariants<T>(
  url: string,
  payloads: Array<Record<string, any>>,
  options?: any
): Promise<T> {
  let lastErr: any = null;
  for (const body of payloads) {
    try {
      const res = await axios.post(url, body, options);
      return res.data as T;
    } catch (e: any) {
      lastErr = e;
      const status = e?.response?.status;
      // Retry only for "bad request" style errors where field names might differ.
      if (status !== 400) break;
    }
  }
  throw lastErr;
}

export async function initiateEmployeeLoginOtp(
  nationalId: string,
  password: string,
  recaptchaToken?: string | null
): Promise<EmployeeInitiateOtpResponse> {
  const nid = (nationalId || '').trim();
  const pw = (password || '').trim();
  const rc = (recaptchaToken || '').trim();
  if (!nid) throw new Error('nationalId is required');
  if (!pw) throw new Error('password is required');

  const url = `${apiBase()}/api/auth/login/initiate-otp`;
  // Backend contract is known (Identifier + Password). Send ONE request only to avoid
  // multiple attempts that can confuse debugging and produce intermittent "empty identifier" logs.
  // ASP.NET Core JSON deserialization is case-insensitive by default, so camelCase is fine.
  const body: Record<string, any> = { identifier: nid, password: pw, ...(rc ? { recaptchaToken: rc } : {}) };
  const res = await axios.post(url, body, { headers: { Accept: 'application/json' } });
  const data = res.data;
  return normalizeInitiateOtpResponse(data);
}

export async function verifyEmployeeLoginOtp(
  challengeId: string,
  otpCode: string,
  password?: string | null
): Promise<EmployeeVerifyOtpResponse> {
  const ch = (challengeId || '').trim();
  const code = (otpCode || '').trim();
  const pw = (password || '').trim();
  if (!ch) throw new Error('challengeId is required');
  if (!code) throw new Error('otpCode is required');

  const url = `${apiBase()}/api/auth/login/verify-otp`;
  const payloads: Array<Record<string, any>> = [
    { challengeId: ch, otpCode: code, ...(pw ? { password: pw } : {}) },
    { challengeId: ch, code: code, ...(pw ? { password: pw } : {}) },
    { challengeId: ch, otp: code, ...(pw ? { password: pw } : {}) },
    { challengeId: ch, otpCode: code, ...(pw ? { pass: pw } : {}) },
  ];

  const data = await postWithPayloadVariants<any>(url, payloads, {
    headers: { Accept: 'application/json' },
  });
  return normalizeVerifyOtpResponse(data);
}

export async function fetchEmployeeProfile(token) {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/employee/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching employee profile:', error);
    throw error;
  }
};

export const getEmployeeProfile = async () => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token found');

  // ODD Default// const response = await axios.get(`${API_BASE_URL}/api/employee/profile`, {
  const response = await axios.get(`${API_BASE_URL}/api/employee/personalInfo`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data; // ✅ Add this
};


export const getYaqeenInfo = async () => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token found');

  const response = await axios.get(`${API_BASE_URL}/api/getYaqeenInfo`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

export const verifyYaqeen = async ({ identificationId, dateOfBirth }) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token found');

  const response = await axios.post(`${API_BASE_URL}/api/yaqeen/verify`, {
    identificationId,
    dateOfBirth
  }, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};


export const getEmployeeContactInfo = async () => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token found');

  try {
    const response = await axios.get(`${API_BASE_URL}/api/employee/contactInfo`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      // optional: timeout: 10000,
    });

    return response.data;
  } catch (err) {
    // Better error message for debugging
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const payload = err.response?.data;
      const msg = payload?.message || payload?.error || err.message || err.toString();
      throw new Error(`Failed to fetch contact info${status ? ` (HTTP ${status})` : ''}: ${msg}`);
    }
    throw err;
  }
};





export const updateEmployeeContactInfo = async (contactPayload) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token found');

  try {
    const res = await axios.put(`${API_BASE_URL}/api/employee/contact`, contactPayload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return res.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const payload = err.response?.data;
      const msg = payload?.message || payload?.error || err.message;
      throw new Error(`Failed to update contact${status ? ` (HTTP ${status})` : ''}: ${msg}`);
    }
    throw err;
  }
};




// =============================================
// Author:      JUNAID HASSAN
// Create date: 2025-08-17
// Description: NEW overlay-first Contact API helpers.
//              - fetchContactOverlay(): GET { data, flags, pendingCount, ... }
//              - saveContactDraft(): POST draft (snake_case), then re-GET overlay
// Notes:
//   * These are the ones your new UI should call.
//   * Old getEmployeeContactInfo/updateEmployeeContactInfo can stay for now.
// =============================================

export const fetchContactOverlay = async () => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token found');

  try {
    const response = await axios.get(`${API_BASE_URL}/api/employee/contactInfo`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });
    return response.data; // { requestId, pendingCount, baselinePresent, lastSavedAt, data, flags }
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const payload = err.response?.data;
      const msg = payload?.message || payload?.error || err.message || String(err);
      throw new Error(`Failed to fetch contact overlay${status ? ` (HTTP ${status})` : ''}: ${msg}`);
    }
    throw err;
  }
};

export const saveContactDraft = async (model) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token found');

  // Accept snake_case (new UI) and gracefully fallback from camelCase (old UI)
  const body = {
    email:           model?.email ?? null,
    mobile:          model?.mobile ?? model?.mobileNumber ?? null,
    other_mobile:    model?.other_mobile ?? model?.otherMobileNumber ?? null,
    emergency_name:  model?.emergency_name ?? model?.emergencyContactName ?? null,
    emergency_phone: model?.emergency_phone ?? model?.emergencyPhoneNumber ?? null,
  };

  try {
    // Save as DRAFT (does not touch baseline)
    await axios.post(`${API_BASE_URL}/api/employee/tabs/contact/draft`, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    // Re-read overlay so UI shows latest values + flags
    return await fetchContactOverlay();
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const payload = err.response?.data;
      const msg = payload?.message || payload?.error || err.message || String(err);
      throw new Error(`Failed to save contact draft${status ? ` (HTTP ${status})` : ''}: ${msg}`);
    }
    throw err;
  }
};

// =============================================
// Author:      JUNAID HASSAN
// Create date: 2025-08-18
// Description: Personal overlay endpoints (GET + DRAFT POST)
// =============================================
export const fetchPersonalOverlay = async () => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token found');

  try {
    const res = await axios.get(`${API_BASE_URL}/api/employee/personal/overlay`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });
    return res.data; // { requestId, pendingCount, baselinePresent, lastSavedAt, data, flags }
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const payload = err.response?.data;
      const msg = payload?.message || payload?.error || err.message || err.toString();
      throw new Error(`Failed to fetch personal overlay${status ? ` (HTTP ${status})` : ''}: ${msg}`);
    }
    throw err;
  }
};

export const savePersonalDraft = async (personalPayload) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token found');

  try {
    const res = await axios.post(`${API_BASE_URL}/api/employee/personal/draft`, personalPayload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    return res.data; // { requestId, pendingCount, lastSavedAt, data, flags }
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const payload = err.response?.data;
      const msg = payload?.message || payload?.error || err.message || err.toString();
      throw new Error(`Failed to save personal draft${status ? ` (HTTP ${status})` : ''}: ${msg}`);
    }
    throw err;
  }
};

// Get the server-built review snapshot for the current employee.
// Returns:
// {
//   meta: {...},
//   overlays: { personal, contact, address, employment },
//   request_vals: { ... }
// }
export const getReviewSnapshot = async () => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token found');

  try {
    const res = await axios.get(`${API_BASE_URL}/api/employee/review-snapshot`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Cache-Control': 'no-store',
      },
    });
    return res.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const payload = err.response?.data;
      const msg = payload?.message || payload?.error || err.message || String(err);
      throw new Error(
        `Failed to load review snapshot${status ? ` (HTTP ${status})` : ''}: ${msg}`
      );
    }
    throw err;
  }
};


/**
 * Submit the employee update request.
 * - If `requestVals` is provided, it sends { request_vals: ... }.
 * - If omitted/null, the server builds from pending drafts in SQL.
 *   (JWT provides eid + odoo_session; we don't change JWT.)
 *
 * Returns: { requestId, odooRequestId, odooState, submittedAt }
 */
export const submitUpdateRequest = async (requestVals) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token found');

  const url = `${API_BASE_URL}/api/employee/submit-update-request`;
  const body =
    requestVals && Object.keys(requestVals).length > 0
      ? { request_vals: requestVals }
      : undefined; // no body → server uses drafts

  try {
    const res = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    return res.data; // { requestId, odooRequestId, odooState, submittedAt }
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const payload = err.response?.data;
      const msg =
        payload?.message ||
        payload?.error ||
        err.message ||
        String(err);
      throw new Error(
        `Failed to submit update request${status ? ` (HTTP ${status})` : ''}: ${msg}`
      );
    }
    throw err;
  }
};
